import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import Loader from "../../../Components/Common/Loader";
import CommentList from "./CommentList";
import { useUserStore } from "../../../store/store";
import PrevInquiryListSection from "./PrevInquiryListSection";
import { IoArrowBackOutline } from "react-icons/io5";
import { IoIosArrowBack } from "react-icons/io";
import NavBottomBar from "../../../Components/Common/NavBottomBar";
import Navbar from "../../../Components/Common/navbar";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import { BACKEND_HOST } from "../../../api";
import { inquiryService } from '../../../api/services/inquiry.service';
import { sanitizeRichHtml } from "../../../utils/sanitizeRichHtml";


const InquiryDetailSection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [user, permission] = useUserStore((state) => [state.user, state.permission]);
  const capabilitySet = useMemo(
    () => new Set(Array.isArray(user?.capabilities) ? user.capabilities : []),
    [user?.capabilities]
  );
  const hasCapability = (capability) =>
    Boolean(user?.is_global_admin || capabilitySet.has(capability));
  const hasLegacySitePermission = permission && typeof permission.permission === "number";
  const isSiteStaff = hasCapability("site_staff") || hasLegacySitePermission;
  const isSiteManager =
    hasCapability("site_manager") ||
    hasCapability("site_owner") ||
    (typeof permission?.permission === "number" && [1, 2].includes(permission.permission));

  const [deleteModal, setDeleteModal] = useState(false);
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch Logic
  const fetchInquiry = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inquiryService.getArticleById(id);
      const data = response.data;
      if (data.error) {
        setError(data.error);
      } else {
        setInquiry(data);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch inquiry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiry();
  }, [id]);

  const deleteInquiry = async () => {
    try {
      const response = await inquiryService.deleteArticleById(id);
      if (response.status === 204) {
        navigate(`/inquiry`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setExtraInfo = async (articleId, need, extraInfo) => {
    try {
      await inquiryService.postInquiryExtraInfo(articleId, need, extraInfo);
      fetchInquiry();
    } catch (error) {
      console.error(error);
    }
  };

  const article_username = useMemo(() => {
    if (!inquiry) return "";
    let _username = "";
    if (isSiteStaff || (user && inquiry.user_id == user.id)) {
      _username = inquiry.username;
    } else {
      _username = `${inquiry.username[0] + "*".repeat(Math.max(0, inquiry.username.length - 1))}`;
    }

    if (!!inquiry.user_permission?.permission) {
      if (isSiteStaff) {
        _username = `관리자(${_username})`;
      } else {
        _username = `관리자`;
      }
    }
    return _username;
  }, [inquiry, isSiteStaff, user]);

  const safeInquiryContent = useMemo(() => {
    return sanitizeRichHtml(inquiry?.content);
  }, [inquiry?.content]);


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader loading={loading} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-4 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 flex items-center text-gray-600 hover:text-black transition-colors"
          >
            <IoIosArrowBack size={24} />
            <span className="text-sm font-medium ml-1">목록으로</span>
          </button>
          <div className="text-center mt-20">
            <p className="text-lg font-medium text-gray-900 mb-2" style={{ fontFamily: "Gmarket-M" }}>
              글쓴이 혹은 관리자만 글을 볼 수 있습니다.
            </p>
            <p className="text-xs text-gray-500">{error}</p>
          </div>
        </div>
        <NavBottomBar />
      </div>
    );
  }

  if (!inquiry) return null;

  const isOwnerOrAdmin = isSiteStaff || (user && Number(user.id) === Number(inquiry.user_id));
  const isAdmin = isSiteManager;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Navbar />

      <div className="max-w-screen-md mx-auto px-4 sm:px-6 mt-8">

        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm py-4 mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(`/inquiry?${params.toString()}`)}
            className="p-2 -ml-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-full transition-all"
          >
            <IoArrowBackOutline size={24} />
          </button>
          <h1 className="absolute left-1/2 transform -translate-x-1/2 text-lg font-bold text-gray-900" style={{ fontFamily: "Gmarket-M" }}>
            문의글
          </h1>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Article Header */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 break-words leading-snug">
              {inquiry.title}
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={inquiry.user_permission?.permission ? "/amazingpark-logo.png" : inquiry.profile_img || `${BACKEND_HOST}/media/ProfileBlank.png`}
                  alt="profile"
                  className="w-10 h-10 rounded-full object-cover border border-gray-100"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {article_username}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {moment(inquiry.written_date).format("YYYY-MM-DD HH:mm")}
                  </p>
                </div>
              </div>

              {isOwnerOrAdmin && (
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <button
                      onClick={() => {
                        if (inquiry.need_extra_info) {
                          setExtraInfo(inquiry.id, false, null);
                        } else {
                          const result = window.prompt("주의할 내용을 입력해주세요.");
                          if (result) setExtraInfo(inquiry.id, true, result);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${inquiry.need_extra_info
                        ? "bg-green-50 text-green-600 hover:bg-green-100"
                        : "bg-red-50 text-red-600 hover:bg-red-100"
                        }`}
                      style={{ fontFamily: "Gmarket-M" }}
                    >
                      {inquiry.need_extra_info ? "주의해제" : "주의설정"}
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/inquiry/form/${id}`)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors"
                    style={{ fontFamily: "Gmarket-M" }}
                  >
                    수정
                  </button>
                  <button
                    onClick={() => setDeleteModal(true)}
                    className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold transition-colors"
                    style={{ fontFamily: "Gmarket-M" }}
                  >
                    삭제
                  </button>
                </div>
              )}
            </div>

            {/* Meta Info */}
            <div className="mt-4 space-y-1 text-sm text-gray-600">
              {isSiteStaff && inquiry.phone_num && (
                <p>연락처: <a href={`tel:${inquiry.phone_num}`} className="text-blue-500 hover:underline">{inquiry.phone_num}</a></p>
              )}
              {SUBDOMAIN === "test" && isSiteStaff && (
                <p className="text-[#f6475f]">{inquiry.site}</p>
              )}
              {inquiry.address && <p>문의주소: {inquiry.address}</p>}
              {isAdmin && inquiry.need_extra_info && (
                <p className="text-red-500 font-medium mt-2 bg-red-50 p-2 rounded-lg inline-block">
                  주의: {inquiry.extra_info || "주의가 필요합니다."}
                </p>
              )}
            </div>
          </div>

          {/* Content Body */}
          <div className="p-6">
            {inquiry.images && inquiry.images.length > 0 && (
              <div className="mb-6 space-y-4">
                {inquiry.images.map((image) => (
                  <img
                    key={image.id}
                    src={image.image}
                    alt="Inquiry attachment"
                    className="w-full h-auto rounded-xl border border-gray-100"
                  />
                ))}
              </div>
            )}

            <div
              className="prose prose-sm max-w-none text-gray-800 leading-relaxed break-words"
              dangerouslySetInnerHTML={{ __html: safeInquiryContent }}
            />
          </div>

          {/* Comments */}
          {inquiry.mode !== "4" && (
            <div className="border-t border-gray-100 bg-gray-50/50 p-6">
              <CommentList articleId={inquiry.id} />
            </div>
          )}
        </div>

        {/* Previous Inquiries (Admin Only) */}
        {isAdmin && (
          <div className="mt-8 mb-12">
            <h3 className="text-sm font-bold text-gray-500 mb-3 ml-1" style={{ fontFamily: "Gmarket-M" }}>
              {inquiry.username}님의 이전 문의글
            </h3>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <PrevInquiryListSection targetUser={inquiry.phone_num} targetSubdomain={inquiry.subdomain} />
            </div>
          </div>
        )}

      </div>

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setDeleteModal(false)} aria-hidden="true"></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex flex-col items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 text-center" id="modal-title" style={{ fontFamily: "Gmarket-M" }}>
                      삭제하기
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 text-center">
                        해당 글을 삭제하시겠습니까? <br />삭제된 글은 복구할 수 없습니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse justify-center gap-2">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    deleteInquiry();
                    setDeleteModal(false);
                  }}
                  style={{ fontFamily: "Gmarket-M" }}
                >
                  삭제하기
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setDeleteModal(false)}
                  style={{ fontFamily: "Gmarket-M" }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryDetailSection;
